import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { EmspUserInvoiceBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/emsp-user-invoice-base.model';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import {
  useEmspBoGetInvoiceQuery,
  useLazyEmspBoGetInvoicePdfQuery,
} from '@app/core/service/emsp-bo-invoice.service';
import { useSetPageTitleContext } from '@app/shared/layout/page-layout/page-layout-title.context-provider';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';

import { downloadEmspBoInvoicePdf } from '../emsp-bo-invoice-pdf.helper';
import { EmspBoInvoiceContextProvider } from '../emsp-bo-invoice.context';
import { EmspBoInvoiceDetailsLoadingView } from './emsp-bo-invoice-details-loading.view';
import { EmspBoInvoiceDetailsView } from './emsp-bo-invoice-details.view';

interface EmspInvoicesDetailsControllerProps {
  resource: EmspUserInvoiceBaseModel;
}

const EmspBoInvoiceDetailsController = (
  props: EmspInvoicesDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [lazyGetInvoicePdfQuery] = useLazyEmspBoGetInvoicePdfQuery();

  const handleDownloadInvoicePdf = useCallback(async () => {
    try {
      const { isError, isSuccess, data, error } = await lazyGetInvoicePdfQuery({
        invoiceId: resource.id,
      });

      if (isError) {
        throw new Error(JSON.stringify(error));
      }

      if (isSuccess) {
        await downloadEmspBoInvoicePdf(resource, data);
        toast.success(t('$.emsp-bo-invoice-download-pdf.success'));
      }
    } catch (error: any) {
      toast.error(
        t('$.emsp-bo-invoice-download-pdf.error', {
          error: error.toString(),
        }),
      );
    }
  }, [lazyGetInvoicePdfQuery, resource, t]);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.invoiceNumber);
  }, [resource.invoiceNumber, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.DOWNLOAD_INVOICE_PDF,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="download-pdf"
          onClick={handleDownloadInvoicePdf}
          icon="download-01"
        />,
      );
    }

    setPageAction(actions);
  }, [handleDownloadInvoicePdf, setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <EmspBoInvoiceContextProvider data={resource}>
      <EmspBoInvoiceDetailsView />
    </EmspBoInvoiceContextProvider>
  );
};

export const EmspBoInvoiceDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useEmspBoGetInvoiceQuery({ invoiceId: params.id ?? '' });

  const { Component } = withProtectedResource<
    EmspUserInvoiceBaseModel,
    {},
    EmspUserInvoiceBaseModel
  >(EmspBoInvoiceDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.READ_INVOICE,
      }),
    loadingComponent: <EmspBoInvoiceDetailsLoadingView />,
  })({});

  return Component;
};
